/**
 * Persist draft rack rows created before the parent request is saved (#995 / #1052).
 */

import { api } from './api';
import { networkService } from './network';
import { offlineCache } from './offlineCache';
import { toCreateRackPayload } from './rackPayload';
import {
  stableDraftInstanceId,
  withDraftDimensionUnitTempId,
} from '../request/dimensionUnitChoices';

import type { RackConfiguration } from '../types/rack';

export async function persistDraftRackConfigurations(
  requestId: string | number,
  racks: RackConfiguration[],
): Promise<RackConfiguration[]> {
  const createdIds: number[] = [];
  const createdRacks: RackConfiguration[] = [];
  const idKey = String(requestId);

  try {
    for (const rack of racks) {
      const created = await api.post<RackConfiguration>(
        `/requests/${requestId}/racks`,
        withDraftDimensionUnitTempId(rack.id, toCreateRackPayload(rack)),
      );
      if (created.id) {
        createdIds.push(created.id);
      }
      createdRacks.push(created);
    }
    return createdRacks;
  } catch (error) {
    for (const rackId of createdIds) {
      try {
        await api.delete(`/requests/${idKey}/racks/${rackId}`);
      } catch (rollbackError) {
        console.error(
          'Failed to roll back rack after partial draft flush:',
          rollbackError,
        );
      }
    }
    throw error;
  }
}

/**
 * Flush draft racks after Create Request save — online POST or offline cache + queue.
 */
export async function flushDraftRackConfigurations(
  requestId: string | number,
  racks: RackConfiguration[],
  isOfflineRequest = false,
): Promise<RackConfiguration[]> {
  if (!racks.length) {
    return [];
  }

  const id = String(requestId);

  if (isOfflineRequest || !networkService.isConnected()) {
    const normalized = racks.map((rack, index) => ({
      ...toCreateRackPayload(rack),
      id: stableDraftInstanceId(rack.id, index),
      requestId: typeof requestId === 'number' ? requestId : 0,
      displayOrder: rack.displayOrder ?? index + 1,
      rackStatus: rack.rackStatus ?? 'new',
    })) as RackConfiguration[];

    await offlineCache.updateCachedRequestRacks(id, normalized);
    for (const rack of normalized) {
      await offlineCache.addRackToOfflineQueue(
        id,
        'create',
        withDraftDimensionUnitTempId(rack.id, toCreateRackPayload(rack)),
        rack.id,
      );
    }
    return normalized;
  }

  return persistDraftRackConfigurations(requestId, racks);
}
